import { API_BASE_URL } from '../../../config';
import type {
  FeedResponse,
  FollowListResponse,
  LikeResponse,
  LikeTargetType,
  CommentsResponse,
  Comment,
  CreateCommentData,
  UserProfile,
  UserSearchResponse,
} from '../types';

const API_BASE = `${API_BASE_URL}/api/v1/social`;

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// --- Feed ---

export async function fetchFeed(cursor?: string): Promise<FeedResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set('cursor', cursor);
  const response = await fetch(`${API_BASE}/feed?${params.toString()}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<FeedResponse>(response);
}

// --- Follow ---

export async function followUser(userId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/follow/${userId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  return handleResponse<void>(response);
}

export async function unfollowUser(userId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/follow/${userId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return handleResponse<void>(response);
}

export async function fetchFollowers(page = 1, pageSize = 20): Promise<FollowListResponse> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  const response = await fetch(`${API_BASE}/followers?${params.toString()}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<FollowListResponse>(response);
}

export async function fetchFollowing(page = 1, pageSize = 20): Promise<FollowListResponse> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  const response = await fetch(`${API_BASE}/following?${params.toString()}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<FollowListResponse>(response);
}

// --- Likes ---

export async function likeTarget(targetType: LikeTargetType, targetId: string): Promise<LikeResponse> {
  const response = await fetch(`${API_BASE}/likes/${targetType}/${targetId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  return handleResponse<LikeResponse>(response);
}

export async function unlikeTarget(targetType: LikeTargetType, targetId: string): Promise<LikeResponse> {
  const response = await fetch(`${API_BASE}/likes/${targetType}/${targetId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return handleResponse<LikeResponse>(response);
}

// --- Comments ---

export async function fetchComments(
  photoId: string,
  page = 1,
  pageSize = 20,
): Promise<CommentsResponse> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  const response = await fetch(`${API_BASE}/comments/photo/${photoId}?${params.toString()}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<CommentsResponse>(response);
}

export async function createComment(photoId: string, data: CreateCommentData): Promise<Comment> {
  const response = await fetch(`${API_BASE}/comments/photo/${photoId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse<Comment>(response);
}

export async function deleteComment(commentId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/comments/${commentId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return handleResponse<void>(response);
}

// --- User Profile ---

export async function fetchUserProfile(userId: string): Promise<UserProfile> {
  const response = await fetch(`${API_BASE_URL}/api/v1//api/v1/users/${userId}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<UserProfile>(response);
}

// --- User Search ---

export async function searchUsers(query: string): Promise<UserSearchResponse> {
  const params = new URLSearchParams({ q: query });
  const response = await fetch(`${API_BASE_URL}/api/v1//api/v1/search/users?${params.toString()}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<UserSearchResponse>(response);
}
